import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User as UserIcon, LayoutDashboard } from "lucide-react";
import { BrandMark } from "./BrandMark";
import { useAuth } from "../context/AuthContext";
import { TID } from "../lib/testIds";

const BACKOFFICE_URL = process.env.REACT_APP_BACKOFFICE_URL;

const linkClass = ({ isActive }) =>
  `label-caps px-3 py-2 rounded-md transition-colors ${
    isActive ? "text-platinum" : "text-white/70 hover:text-white"
  }`;

export default function Navbar() {
  const { user, login, logout, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      data-testid={TID.nav.root}
      className="glass fixed top-0 inset-x-0 z-50"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-3 flex items-center justify-between">
        <Link to="/" data-testid={TID.nav.logo} className="flex items-center gap-3">
          <BrandMark size={38} />
          <div className="hidden sm:block leading-none">
            <div className="font-serif text-lg text-white">Plenty Rydez</div>
            <div className="label-caps text-gold/80 mt-0.5">NY · NJ · PA · DMV · CT</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          <NavLink to="/" end className={linkClass} data-testid={TID.nav.home}>Home</NavLink>
          <NavLink to="/fleet" className={linkClass} data-testid={TID.nav.fleet}>Fleet</NavLink>
          <NavLink to="/driver-rentals" className={linkClass} data-testid="nav-driver-rentals">Drive with us</NavLink>
          <NavLink to="/about" className={linkClass} data-testid="nav-about">About</NavLink>
          <NavLink to="/find" className={linkClass} data-testid="nav-find-booking">Find booking</NavLink>
          {user && (
            <NavLink to="/bookings" className={linkClass} data-testid={TID.nav.bookings}>Bookings</NavLink>
          )}
          <NavLink to="/support" className={linkClass} data-testid={TID.nav.support}>Support</NavLink>
          {(user?.role === "staff" || user?.role === "admin") && (
            <NavLink
              to="/backoffice"
              data-testid={TID.nav.backoffice}
              className={({ isActive }) =>
                `label-caps px-3 py-2 rounded-md transition-colors flex items-center gap-1.5 ${
                  isActive ? "text-burgundy" : "text-burgundy/80 hover:text-burgundy"
                }`
              }
            >
              <LayoutDashboard size={12} />
              Back office
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {loading ? (
            <div className="w-24 h-9 rounded-full bg-ink-700 animate-pulse" />
          ) : user ? (
            <div className="flex items-center gap-2">
              <button
                data-testid={TID.nav.profile}
                onClick={() => navigate("/profile")}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-ink-700 hover:bg-ink-600 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <UserIcon size={16} className="text-gold" />
                )}
                <span className="text-sm">{user.name?.split(" ")[0]}</span>
              </button>
              <button
                data-testid={TID.nav.logout}
                onClick={logout}
                className="p-2 rounded-full text-white/60 hover:text-white hover:bg-ink-700 transition-colors"
                aria-label="Log out"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <button
              data-testid={TID.nav.login}
              onClick={login}
              className="btn-gold px-5 py-2 rounded-full text-sm transition-all"
            >
              Sign in
            </button>
          )}
        </div>

        <button
          className="md:hidden p-2 text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 px-5 py-4 space-y-2 bg-ink-900/95">
          <NavLink to="/" end className={linkClass} onClick={() => setOpen(false)}>Home</NavLink>
          <NavLink to="/fleet" className={linkClass} onClick={() => setOpen(false)}>Fleet</NavLink>
          <NavLink to="/driver-rentals" data-testid="nav-driver-rentals" className={linkClass} onClick={() => setOpen(false)}>Drive with us</NavLink>
          <NavLink to="/about" data-testid="nav-about-mobile" className={linkClass} onClick={() => setOpen(false)}>About</NavLink>
          <NavLink to="/find" data-testid="nav-find-booking-mobile" className={linkClass} onClick={() => setOpen(false)}>Find booking</NavLink>
          {user && (
            <NavLink to="/bookings" className={linkClass} onClick={() => setOpen(false)}>Bookings</NavLink>
          )}
          <NavLink to="/support" className={linkClass} onClick={() => setOpen(false)}>Support</NavLink>
          {(user?.role === "staff" || user?.role === "admin") && (
            <NavLink to="/backoffice" data-testid={TID.nav.backoffice} className={linkClass} onClick={() => setOpen(false)}>
              Back office
            </NavLink>
          )}
          <div className="pt-3">
            {user ? (
              <button onClick={logout} className="btn-gold px-4 py-2 rounded-full text-sm w-full">
                Log out
              </button>
            ) : (
              <button onClick={login} className="btn-gold px-4 py-2 rounded-full text-sm w-full">
                Sign in
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
